import React from "react";
import { AssetRecordType, TLAssetId, TLShapeId, useEditor } from "@tldraw/tldraw";
import { createShapeId } from "@tldraw/editor";
import { makeStyles, Select } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import _ from "lodash";
import QRCode from "qrcode";
import { defaultQrCode } from "pages/flyer/builder/drawer/new-tldraw-components/consts";
import { DocumentOrientation, TemplateConfig, PossibleFlyerType } from "pages/flyer/builder/drawer/types";
import useQRCodePosition from "Hooks/useQRCodePosition";
import { FORMATS } from "pages/flyer/builder/constants";
import { Input } from "components/form";
import { generatePath, useParams } from "react-router-dom";
import InfoIcon from "static/media/info";
import { QrCheckbox } from "components/QrCheckbox";
import { useStore } from "../../../../../../../../store";
import { StoreType } from "../../../../../../../../store/types";
import { updateCampaignExtraData, getCampaign } from "../../../../../../../../graphQL";
import CopyIcon from "components/icons/components/CopyIcon";
import { getCosmeticQrShortLink } from "utils/qrShortLink";
import { resetQrShapesToDefault } from "utils/qrCode";

const libraries: any = ["geometry", "places", "drawing"];
// const printLinesViewSelector = (s: TDSnapshot) => s.document.isShowGuidelines;

const options = ["Select QR code from tool"];

const QrCodeComponent = () => {
  const classes = useStyles();
  const editor = useEditor();
  const { format, campaignId: urlCampaignId, clientId: urlClientId, roomId: designId } = useParams();

  const [currentFormatConfig, setCurrentFormatConfig] = React.useState<TemplateConfig>(
    FORMATS[format as PossibleFlyerType] || ({} as TemplateConfig)
  );

  const {
    campaign: { qrCodeLink, id: campaignId },
    client: { id: clientId },
    qrCodeSettings: {
      // hideQrBackground,
      hideQrCoupon,
    },
    updateCampaign,
    updateQrCodeSettings,
    currentOrientations,
  }: StoreType = useStore();

  const { x, y, w, h, assetId, isPortrait, frontShape, wh } = useQRCodePosition({
    editor,
    format: format as PossibleFlyerType,
    currentFormatConfig,
    designId,
    campaignId: urlCampaignId || campaignId,
    currentOrientation: currentOrientations,
  });

  // Helper function to update QR code shapes with new QR link
  // Note: This function updates all QR codes, but typically you'd want to update only the selected one
  // For now, it updates all QR codes that match the link being saved
  const updateQrCodeShapes = React.useCallback(
    (qrLink: string) => {
      try {
        // Find all QR code shapes and update their qrCodeLink property
        const allShapes = editor.getCurrentPageShapes();
        const qrCodeShapes = allShapes.filter(
          (shape) =>
            shape.id.includes("qrCode") ||
            (shape.type === "customImage" && (shape.props as any)?.qrCodeLink) ||
            (shape.type === "customImage" && (shape.props as any)?.assetId?.toString().includes("qr"))
        );

        if (qrCodeShapes.length > 0) {
          qrCodeShapes.forEach((shape) => {
            // Only update if the shape doesn't have a qrCodeLink or if it matches the current link
            const currentLink = (shape.props as any)?.qrCodeLink;
            if (!currentLink || currentLink === qrLink) {
              editor.updateShape({
                ...shape,
                props: {
                  ...shape.props,
                  qrCodeLink: qrLink,
                },
              });
            }
          });
        }
      } catch (error) {
        console.warn("Error updating QR code shapes:", error);
      }
    },
    [editor]
  );

  // Helper function to extract QR link from shape properties or data attributes
  const getQrLinkFromShapes = React.useCallback(() => {
    try {
      // First, try to get QR link from shape properties
      const allShapes = editor.getCurrentPageShapes();
      const qrCodeShapes = allShapes.filter(
        (shape) =>
          shape.id.includes("qrCode") ||
          (shape.type === "customImage" && (shape.props as any)?.qrCodeLink) ||
          (shape.type === "customImage" && (shape.props as any)?.assetId?.toString().includes("qr"))
      );

      // Get the most recent QR code link (last one in the array)
      for (let i = qrCodeShapes.length - 1; i >= 0; i--) {
        const shape = qrCodeShapes[i];
        const shapeQrLink = (shape.props as any)?.qrCodeLink;
        if (shapeQrLink && shapeQrLink.trim() && shapeQrLink !== "https://" && shapeQrLink !== "http://") {
          return shapeQrLink.trim();
        }
      }

      // Fallback: Try to find QR code images with data-qr-link attribute
      const qrImages = document.querySelectorAll('img[data-qr-code="true"]');
      for (let i = 0; i < qrImages.length; i++) {
        const img = qrImages[i];
        const qrLink = img.getAttribute("data-qr-link");
        if (qrLink && qrLink.trim() && qrLink !== "https://" && qrLink !== "http://") {
          return qrLink.trim();
        }
      }

      return null;
    } catch (error) {
      console.warn("Error reading QR link from shapes or data attributes:", error);
      return null;
    }
  }, [editor]);

  // Get initial value: campaign data first, then fallback to shape properties
  const getInitialQrValue = React.useCallback(() => {
    const campaignQrLink = qrCodeLink?.trim();
    if (campaignQrLink && campaignQrLink !== "https://" && campaignQrLink !== "http://") {
      return campaignQrLink;
    }

    // Fallback to shape properties or data attributes from QR code images
    const shapeQrLink = getQrLinkFromShapes();
    return shapeQrLink || "";
  }, [qrCodeLink, getQrLinkFromShapes]);

  // Local state for input value (to handle user typing before saving). Always the RAW link.
  const [inputValue, setInputValue] = React.useState(getInitialQrValue);
  const [copiedTooltipOpen, setCopiedTooltipOpen] = React.useState(false);

  // Ref to track if input is focused to prevent unfocusing during typing
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isInputFocused = React.useRef(false);

  // Sync input with store value when qrCodeLink changes from external sources
  React.useEffect(() => {
    // Don't update if the input is currently focused (user is typing)
    if (isInputFocused.current) {
      return;
    }

    const newValue = getInitialQrValue();
    if (newValue !== inputValue) {
      setInputValue(newValue);
    }
  }, [getInitialQrValue, inputValue]);

  // Periodically check for QR code shapes with embedded links (for dynamically added QR codes)
  React.useEffect(() => {
    const checkForQrShapeData = () => {
      // Don't update if the input is currently focused (user is typing)
      if (isInputFocused.current) {
        return;
      }

      // Only check if we don't have a campaign QR link
      const campaignQrLink = qrCodeLink?.trim();
      if (campaignQrLink && campaignQrLink !== "https://" && campaignQrLink !== "http://") {
        return; // Campaign data takes precedence
      }

      const shapeQrLink = getQrLinkFromShapes();
      if (shapeQrLink && shapeQrLink !== inputValue) {
        setInputValue(shapeQrLink);
        // Also update the campaign store with the found QR link
        updateCampaign({ qrCodeLink: shapeQrLink });
      }
    };

    // Check immediately and then every 2 seconds for new QR code shapes
    checkForQrShapeData();
    const interval = setInterval(checkForQrShapeData, 2000);

    return () => clearInterval(interval);
  }, [qrCodeLink, inputValue, getQrLinkFromShapes, updateCampaign]);

  // Debounced save function to persist QR code link
  const debouncedSave = React.useCallback(
    _.debounce(async (value: string) => {
      if (!campaignId || !clientId) return;

      try {
        // Add protocol if missing
        const qrCodeLinkWithProtocol = value?.length
          ? value?.startsWith("https://") || value?.startsWith("http://")
            ? value
            : `https://${value}`
          : null;

        await updateCampaignExtraData({
          campaignId,
          qrCodeLink: qrCodeLinkWithProtocol,
        });

        // Update store state after successful save
        updateCampaign({ qrCodeLink: qrCodeLinkWithProtocol });

        // Update QR shapes + re-sync from the backend ONLY when we saved an actual link.
        // When clearing (null), skip both: the getCampaign round-trip can return a stale
        // non-null value before the write propagates and would otherwise re-stamp the old
        // link onto the shapes we just cleared.
        if (qrCodeLinkWithProtocol) {
          // Update all QR code shapes with the new link (ensures persistence in design)
          updateQrCodeShapes(qrCodeLinkWithProtocol);

          // Trigger a campaign data refresh to ensure store is synced with backend
          // This helps prevent QR links from disappearing on page refresh
          try {
            const refreshedCampaignData = await getCampaign(campaignId, clientId);
            if (refreshedCampaignData?.qrCodeLink !== undefined) {
              updateCampaign({ qrCodeLink: refreshedCampaignData.qrCodeLink });
              // Also update shapes with the refreshed data
              if (refreshedCampaignData.qrCodeLink) {
                updateQrCodeShapes(refreshedCampaignData.qrCodeLink);
              }
            }
          } catch (refreshError) {
            console.warn("Could not refresh campaign data after QR save:", refreshError);
          }
        }
      } catch (error) {
        console.error("Error saving QR code link:", error);
      }
    }, 500), // Reduced from 1000ms to 500ms for faster saves
    [campaignId, clientId, updateCampaign, updateQrCodeShapes]
  );

  // Handle input change
  const handleQrCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const onCreateQrCode = async () => {
    // Reset QR code settings to defaults
    updateQrCodeSettings({
      qrPixelColor: "#000000",
      qrBackgroundColor: "",
    });

    // Get the current QR link to embed in the shape
    const currentQrLink = inputValue?.trim() || qrCodeLink;

    // Normalize QR code link (add protocol if missing)
    const normalizedQrLink = currentQrLink?.length
      ? currentQrLink?.startsWith("https://") || currentQrLink?.startsWith("http://")
        ? currentQrLink
        : `https://${currentQrLink}`
      : "";

    // If user entered a QR code link, save it to campaign store as well
    if (normalizedQrLink) {
      debouncedSave(currentQrLink);
    }

    // Build the QR *image* on a cosmetic short link. The raw link is still saved to
    // the shape prop / campaign store (below) so the print pipeline keeps its own
    // tracked shortening.
    const qrImageLink = normalizedQrLink ? await getCosmeticQrShortLink(normalizedQrLink) : "oppizi.com";

    // Generate QR code with default colors (can be changed later via style panel)
    const qrCodeWithTransparentBg = await QRCode.toString(qrImageLink, {
      type: "svg",
      margin: 0,
      width: 300,
      color: {
        dark: "#000000", // Default black pixels
        light: "#0000", // Default transparent background
      },
    });

    // Find existing QR code shapes (all of them, so they share the same URL).
    const allShapes = editor.getCurrentPageShapes();
    const existingQrCodeShapes = allShapes.filter(
      (shape) =>
        shape.id.includes("qrCode") ||
        (shape.type === "customImage" && (shape.props as any)?.qrCodeLink) ||
        (shape.type === "customImage" && (shape.props as any)?.assetId?.toString().includes("qr"))
    );

    if (existingQrCodeShapes.length > 0) {
      // Update every existing QR code so all instances respect the same URL.
      const assetIdsToUpdate = new Set<string>();
      existingQrCodeShapes.forEach((shape) => {
        const existingAssetId = (shape.props as any)?.assetId;
        if (existingAssetId) assetIdsToUpdate.add(existingAssetId);
      });

      if (assetIdsToUpdate.size > 0) {
        editor.updateAssets(
          Array.from(assetIdsToUpdate).map((assetId) => ({
            id: assetId as TLAssetId,
            type: "image",
            typeName: "asset",
            props: {
              name: "image",
              src: `data:image/svg+xml;base64,${btoa(qrCodeWithTransparentBg)}`,
              w: 150,
              h: 150,
              mimeType: "image/svg+xml",
              isAnimated: false,
            },
          }))
        );
      }

      existingQrCodeShapes.forEach((shape) => {
        editor.updateShape({
          ...shape,
          props: {
            ...shape.props,
            qrCodeLink: normalizedQrLink,
          },
        });
      });

      // Switch to select tool and select the first updated QR code
      editor.setCurrentTool("select");
      editor.select(existingQrCodeShapes[0].id);
    } else {
      // Create new QR code if none exists
      const uniqueShapeId = createShapeId();
      const uniqueAssetId = AssetRecordType.createId(`qrCode-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

      editor.createAssets([
        {
          id: uniqueAssetId as TLAssetId,
          type: "image",
          typeName: "asset",
          meta: {},
          props: {
            name: "image",
            src: `data:image/svg+xml;base64,${btoa(qrCodeWithTransparentBg)}`,
            w: 150,
            h: 150,
            mimeType: "image/svg+xml",
            isAnimated: false,
          },
        },
      ]);

      editor.createShape({
        id: uniqueShapeId as any,
        type: "customImage",
        x: x,
        y: y,
        props: {
          w,
          h,
          assetId: uniqueAssetId,
          qrCodeLink: normalizedQrLink, // Store QR link directly in shape properties
        },
      });

      // Switch to select tool and select the newly created QR code
      editor.setCurrentTool("select");
      editor.select(uniqueShapeId);
    }

    // Clear the input after creating/updating QR code
    setInputValue("");
  };

  // Cleanup debounced function on unmount
  React.useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return (
    <>
      <div className={classes.settings_row} style={{ padding: "16px" }}>
        <div>
          <label
            className="flex items-center gap-1 font-semibold text-[#2E3860] text-[14px]"
            style={{ marginBottom: "8px" }}
          >
            <p>QR Link</p>
            <Tooltip
              classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
              title={
                <div>
                  Please enter the desired URL, it can be a website address, a landing page or a link to the business
                  address in Google Maps.
                </div>
              }
              placement="bottom"
              arrow
            >
              <div>
                <InfoIcon className={classes.infoIcon} size={16} color="#707087" />
              </div>
            </Tooltip>
          </label>
          <div className="qr-code-input" style={{ position: "relative", height: "37px", marginBottom: "16px" }}>
            <Tooltip
              title={inputValue || ""}
              placement="top"
              arrow
              disableFocusListener
              classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Type URL here"
                value={inputValue}
                onChange={handleQrCodeChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onCreateQrCode();
                  }
                }}
                onFocus={() => {
                  isInputFocused.current = true;
                }}
                onBlur={() => {
                  isInputFocused.current = false;
                  // If the user cleared the link, clear it from the QR shape(s) and re-render the
                  // image to the default QR. Otherwise the shape-sync effects would read the old
                  // link back off the shape and restore it.
                  if (!inputValue.trim()) {
                    void resetQrShapesToDefault(editor);
                  }
                  // Persist the latest value after the user finishes typing
                  debouncedSave(inputValue);
                }}
                style={{ paddingRight: "32px" }}
              />
            </Tooltip>
            {inputValue && (
              <Tooltip
                open={copiedTooltipOpen}
                onClose={() => setCopiedTooltipOpen(false)}
                title="URL copied!"
                placement="top"
                arrow
                classes={{ tooltip: classes.tooltipCopied, arrow: classes.tooltipArrow }}
              >
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await navigator.clipboard.writeText(inputValue);
                      setCopiedTooltipOpen(true);
                      setTimeout(() => {
                        setCopiedTooltipOpen(false);
                      }, 2000);
                    } catch (err) {
                      // Fallback for older browsers
                      const textArea = document.createElement("textarea");
                      textArea.value = inputValue;
                      textArea.style.position = "fixed";
                      textArea.style.left = "-999999px";
                      document.body.appendChild(textArea);
                      textArea.focus();
                      textArea.select();
                      try {
                        document.execCommand("copy");
                        setCopiedTooltipOpen(true);
                        setTimeout(() => {
                          setCopiedTooltipOpen(false);
                        }, 2000);
                      } catch (err) {
                        console.error("Failed to copy:", err);
                      }
                      document.body.removeChild(textArea);
                    }
                  }}
                  type="button"
                  style={{
                    cursor: "pointer",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    right: "8px",
                    height: "100%",
                    top: "0",
                  }}
                  onMouseEnter={() => {
                    if (!copiedTooltipOpen) {
                      // Show tooltip on hover when not showing copied message
                    }
                  }}
                >
                  <CopyIcon />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <button
          onClick={onCreateQrCode}
          disabled={!inputValue || !inputValue.trim()}
          style={{
            border: !inputValue || !inputValue.trim() ? "1px solid #AEAFC7" : "1px solid #EE4360",
            backgroundColor: !inputValue || !inputValue.trim() ? "#AEAFC7" : "#EE4360",
            color: "#FFFFFF",
            cursor: !inputValue || !inputValue.trim() ? "not-allowed" : "pointer",
          }}
          className="w-full text-center text-[16px] text-[#00112B] font-bold rounded-[12px] px-3 py-[10px] disabled:opacity-100"
        >
          Generate QR Code
        </button>
      </div>
    </>
  );
};

export const QrCode = React.memo(QrCodeComponent);

const useStyles = makeStyles({
  settings_row: {
    marginBottom: 16,
  },
  container: {
    background: "var(--color-surface-panel)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    width: 48,
    padding: 8,
  },
  items: {
    display: "grid",
    gap: 4,
    gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
  },
  label: {
    display: "flex",
    fontSize: 14,
    lineHeight: "16px",
    color: "var(--color-foreground-300)",
    marginBottom: 4,
    alignItems: "center",
  },
  required: {
    color: "red",
  },
  requiredTip: {
    color: "red",
    marginRight: "6px",
  },
  labelBlock: {
    display: "flex",
    justifyContent: "space-between",
  },
  infoIcon: {
    marginLeft: "8px",
  },
  tooltip: {
    padding: 10,
    backgroundColor: "#eee",
    color: "#000",
    width: "250px",
  },
  tooltipCopied: {
    padding: "6px 10px",
    backgroundColor: "#eee",
    color: "#000",
    width: "auto",
    maxWidth: "none",
  },
  tooltipArrow: {
    "&::before": {
      color: "#f9f9f9",
    },
  },
  checkboxContainer: { marginTop: "5px", display: "flex", alignItems: "center", cursor: "pointer" },
  checkboxLabel: {
    fontFamily: "Inter, sans-serif",
    color: "#011533",
    fontSize: "14px",
    marginLeft: "5px",
    fontWeight: 400,
    cursor: "pointer",
  },
  selectRoot: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: "#00112B",
      "& fieldset": {
        borderColor: "#E2E2FC",
      },
      "&:hover fieldset": {
        borderColor: "#E2E2FC",
      },
    },
  },
  listPadding: {
    // padding: "0px !important",
    width: "248px",
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "#E2E2FC",
    },
  },
  menuPaper: {
    borderRadius: "4px !important",
  },
  selectBorderRed: {
    "& .MuiOutlinedInput-root": {
      borderColor: "red",
      "& fieldset": {
        borderColor: "red",
      },
      "&:hover fieldset": {
        borderColor: "red",
      },
    },
  },
});
